import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { PRIMARY_COLOR } from "../constants";

export interface DistributionMetric {
  label: string;
  value: ReactNode;
  highlight?: boolean;
}

interface DistributionTileProps {
  icon?: ReactNode;
  title: string;
  countPercentage?: number;
  /** Fração de 0 a 1 da barra inferior, relativa ao maior item da lista. */
  barRatio: number;
  metrics: DistributionMetric[];
}

const METRIC_COLUMNS: Record<number, string> = {
  2: "grid-cols-2",
  3: "grid-cols-3",
};

/** Tile de distribuição de vendas (pagamento, template): título, % do total, métricas e barra. */
export function DistributionTile({
  icon,
  title,
  countPercentage,
  barRatio,
  metrics,
}: DistributionTileProps) {
  const { t } = useTranslation("reports");

  return (
    <div className="relative overflow-hidden rounded-xl border border-colorBorder bg-gradient-to-br from-zinc-900 to-zinc-950 p-4">
      <div
        className="absolute bottom-0 left-0 h-1 transition-all duration-500"
        style={{ width: `${barRatio * 100}%`, backgroundColor: PRIMARY_COLOR }}
      />

      <div className="flex items-center gap-2 mb-3">
        {icon && <div className="p-2 bg-zinc-800 rounded-lg">{icon}</div>}
        <div className="min-w-0">
          <h4 className="text-xs font-semibold text-colorText truncate">{title}</h4>
          <p className="text-[10px] text-muted-foreground">
            {countPercentage?.toFixed(1)}% {t("reports:of_total")}
          </p>
        </div>
      </div>

      <div className={cn("grid gap-2", METRIC_COLUMNS[metrics.length] ?? "grid-cols-2")}>
        {metrics.map((metric) => (
          <div key={metric.label} className="min-w-0">
            <p className="text-[10px] text-muted-foreground">{metric.label}</p>
            <p
              className={cn(
                "text-sm font-bold truncate",
                metric.highlight ? "text-primary" : "text-colorText",
              )}
            >
              {metric.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
